import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { isCapacitorRuntime } from "../config/vars";
import {
    getAppLockEnabled,
    isBiometricLockAvailable,
    verifyBiometricLock,
} from "../utils/appLock";

const SKIP_GATE_PROMPT_KEY = "WR_APP_LOCK_SKIP_GATE_PROMPT";
const LOCK_GRACE_MS_KEY = "WR_APP_LOCK_GRACE_MS";
const DEFAULT_LOCK_GRACE_MS = 60_000;

function readLockGraceMs() {
    try {
        const raw = localStorage.getItem(LOCK_GRACE_MS_KEY);
        const n = Number(raw);
        if (Number.isFinite(n) && n >= 0) return Math.min(n, 30 * 60_000);
    } catch (_) {
        // ignore
    }
    return DEFAULT_LOCK_GRACE_MS;
}

function consumeSkipGatePrompt() {
    try {
        if (sessionStorage.getItem(SKIP_GATE_PROMPT_KEY) === "1") {
            sessionStorage.removeItem(SKIP_GATE_PROMPT_KEY);
            return true;
        }
    } catch (_) {
        // ignore
    }
    return false;
}

export default function AppLockGate({ children }) {
    const location = useLocation();
    const [enabled, setEnabled] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [unlocked, setUnlocked] = useState(true);
    const [error, setError] = useState("");
    const hiddenAtRef = useRef(0);
    const verifyingRef = useRef(false);

    const isProtectedRoute = useMemo(() => {
        return typeof location?.pathname === "string" && location.pathname.startsWith("/main");
    }, [location?.pathname]);

    const refreshEnabled = useCallback(async () => {
        const v = await getAppLockEnabled();
        setEnabled(v);
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const v = await getAppLockEnabled();
                if (!cancelled) setEnabled(v);
            } finally {
                if (!cancelled) setLoaded(true);
            }
        })();

        const onChanged = () => {
            void refreshEnabled();
        };
        window.addEventListener("wr-app-lock-changed", onChanged);

        return () => {
            cancelled = true;
            window.removeEventListener("wr-app-lock-changed", onChanged);
        };
    }, [refreshEnabled]);

    const unlockNow = useCallback(async () => {
        if (verifyingRef.current) return;
        setError("");
        verifyingRef.current = true;
        try {
            const available = await isBiometricLockAvailable();
            if (!available) {
                setError("❌ Біометрія/ПІН недоступні на цьому пристрої");
                return;
            }
            await verifyBiometricLock({
                title: "Розблокувати додаток",
                subtitle: "Підтвердьте особу",
            });
            setUnlocked(true);
            hiddenAtRef.current = 0;
        } catch (e) {
            setError(`❌ ${e?.message || "Не вдалося розблокувати"}`);
        } finally {
            verifyingRef.current = false;
        }
    }, []);

    useEffect(() => {
        if (!loaded) return;

        if (!isCapacitorRuntime() || !enabled || !isProtectedRoute) {
            setUnlocked(true);
            setError("");
            return;
        }

        if (consumeSkipGatePrompt()) {
            setUnlocked(true);
            setError("");
            return;
        }

        setUnlocked(false);
        void unlockNow();
    }, [loaded, enabled, isProtectedRoute, unlockNow]);

    useEffect(() => {
        if (!loaded) return;
        if (!isCapacitorRuntime() || !enabled) return;

        const handler = () => {
            if (document.hidden) {
                hiddenAtRef.current = Date.now();
                return;
            }

            const hiddenAt = hiddenAtRef.current;
            hiddenAtRef.current = 0;
            if (!hiddenAt) return;

            const graceMs = readLockGraceMs();
            if (Date.now() - hiddenAt < graceMs) return;

            setUnlocked(false);
            setError("");
        };

        document.addEventListener("visibilitychange", handler);
        return () => document.removeEventListener("visibilitychange", handler);
    }, [loaded, enabled]);

    if (!loaded) {
        return children;
    }

    if (!isCapacitorRuntime() || !enabled || !isProtectedRoute || unlocked) {
        return children;
    }

    return (
        <div style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            gap: 12,
        }}>
            <h2 style={{ margin: 0 }}>Додаток заблоковано</h2>
            <button type="button" onClick={unlockNow}>Розблокувати</button>
            {error && <p style={{ margin: 0, color: "red" }}>{error}</p>}
        </div>
    );
}
